import React, { useState } from 'react';
import { IMAGES } from '../constants/images';
import { useAirtable } from '../hooks/useAirtable';
import styles from './ContactPage.module.css';
import SEO, { BUSINESS_SCHEMA } from '../components/SEO';

const CONTACT_SCHEMA = {
  '@context': 'https://schema.org',
  '@graph': [
    { ...BUSINESS_SCHEMA, '@id': 'https://movementgreen.com.au/#business' },
    {
      '@type': 'ContactPage',
      '@id':   'https://movementgreen.com.au/contact#webpage',
      url:     'https://movementgreen.com.au/contact',
      name:    'Book a Free Home Energy Assessment | Movement Green Victoria',
      description: 'Book a free energy assessment with Movement Green. Our VEU specialists identify every government rebate your Victorian home qualifies for — heat pump, AC, LED and more.',
      about: { '@id': 'https://movementgreen.com.au/#business' },
    },
  ],
};

const DETAILS = [
  {
    icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="#2C4A1E"/></svg>,
    label: 'Office',
    value: '20 Timberton Way\nTruganina VIC 3029, Melbourne',
  },
  {
    icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" fill="#2C4A1E"/></svg>,
    label: 'Phone',
    value: '+61 482 024 485',
  },
  {
    icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" fill="#2C4A1E"/></svg>,
    label: 'Email',
    value: 'contact@movementgreen.com.au',
  },
  {
    icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke="#2C4A1E" strokeWidth="2"/><path d="M12 7v5l3 3" stroke="#2C4A1E" strokeWidth="2" strokeLinecap="round"/></svg>,
    label: 'Hours',
    value: 'Mon – Fri, 8:00 AM – 6:00 PM AEST',
  },
];

const EMPTY = { firstName: '', lastName: '', email: '', phone: '', interest: '', message: '' };

export default function ContactPage() {
  const [form, setForm] = useState(EMPTY);
  const { submit, status, error, reset } = useAirtable();

  function handle(e) {
    setForm(p => ({ ...p, [e.target.name]: e.target.value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const ok = await submit(form);
    if (ok) setForm(EMPTY);
  }

  return (
    <main className="page-enter">
      <SEO
        title="Book a Free Home Energy Assessment | Movement Green Victoria"
        description="Book a free VEU energy assessment with Movement Green. We identify every government rebate your Victorian home qualifies for — heat pump, reverse-cycle AC, LED lighting and more."
        keywords="book energy assessment Victoria, free VEU assessment, heat pump rebate enquiry Victoria, reverse cycle AC rebate contact Melbourne"
        schema={CONTACT_SCHEMA}
      />

      {/* ── HERO — Green atrium building ────────── */}
      <section className={styles.heroSection}>
        <div className={styles.heroWrap}>
          {/* Left text */}
          <div className={styles.heroLeft}>
            <span className="eyebrow">Reach Out</span>
            <h1 className={`disp-xl ${styles.heroH1}`}>
              Book Your<br/>Free Energy<br/><em>Assessment.</em>
            </h1>
            <p className="body-lg">
              Whether you're a homeowner exploring VEU<sup>*</sup> rebates or a property
              manager looking to upgrade your portfolio — we'd love to hear from you.
            </p>

            <div className={styles.details}>
              {DETAILS.map(d => (
                <div key={d.label} className={styles.detail}>
                  <div className={styles.detailIcon}>{d.icon}</div>
                  <div>
                    <div className={styles.detailLabel}>{d.label}</div>
                    <div className={styles.detailValue} style={{ whiteSpace: 'pre-line' }}>{d.value}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right image — dramatic green atrium */}
          <div className={styles.heroRight}>
            <img src={IMAGES.contactHero} alt="Movement Green building — green living wall atrium" className={styles.heroImg}/>
            <div className={styles.heroImgOverlay}/>
            <div className={styles.heroBadge}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M21 3C21 3 12 3 7 8C2 13 3 21 3 21C3 21 11 22 16 17C21 12 21 3 21 3Z" fill="#B5D96A"/>
              </svg>
              <div>
                <div className={styles.badgeTitle}>Victorian Energy Upgrades</div>
                <div className={styles.badgeBody}>Helping Victorian homes access government rebates</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FORM + MAP ──────────────────────────── */}
      <section className={styles.formSection}>
        <div className={styles.formWrap}>

          {/* Form card */}
          <div className={styles.formCard}>
            {status === 'success' ? (
              <div className={styles.success}>
                <div className={styles.successIcon}>
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                    <circle cx="16" cy="16" r="16" fill="rgba(181,217,106,0.15)"/>
                    <path d="M9 16L13.5 20.5L23 11" stroke="#2C4A1E" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <h2 className={`disp-sm ${styles.successTitle}`}>Message Received!</h2>
                <p className="body-md">Thanks for reaching out. A member of our team will be in touch within one business day.</p>
                <button className="btn btn-primary btn-lg" onClick={reset}>Send Another</button>
              </div>
            ) : (
              <>
                <div className={styles.formHead}>
                  <div>
                    <span className="eyebrow">Get in Touch</span>
                    <h2 className={`disp-sm ${styles.formTitle}`}>Send a Message</h2>
                  </div>
                  <p className="body-sm" style={{ maxWidth: 180, textAlign: 'right' }}>
                    We respond within 1 business day.
                  </p>
                </div>

                <form className={styles.form} onSubmit={handleSubmit} noValidate>
                  <div className={styles.nameRow}>
                    <div className={styles.field}>
                      <label className={styles.label} htmlFor="firstName">First Name</label>
                      <input id="firstName" name="firstName" type="text" required
                        placeholder="Jane" value={form.firstName} onChange={handle}
                        className={styles.input}/>
                    </div>
                    <div className={styles.field}>
                      <label className={styles.label} htmlFor="lastName">Last Name</label>
                      <input id="lastName" name="lastName" type="text" required
                        placeholder="Smith" value={form.lastName} onChange={handle}
                        className={styles.input}/>
                    </div>
                  </div>

                  <div className={styles.row2}>
                    <div className={styles.field}>
                      <label className={styles.label} htmlFor="email">Email Address</label>
                      <input id="email" name="email" type="email" required
                        placeholder="jane@example.com" value={form.email} onChange={handle}
                        className={styles.input}/>
                    </div>
                    <div className={styles.field}>
                      <label className={styles.label} htmlFor="phone">Phone (optional)</label>
                      <input id="phone" name="phone" type="tel"
                        placeholder="+61 4XX XXX XXX" value={form.phone} onChange={handle}
                        className={styles.input}/>
                    </div>
                  </div>

                  <div className={styles.field}>
                    <label className={styles.label} htmlFor="interest">I'm interested in…</label>
                    <select id="interest" name="interest" value={form.interest} onChange={handle} className={styles.select}>
                      <option value="">Select an upgrade</option>
                      <option value="Heat Pump Hot Water">Heat Pump Hot Water</option>
                      <option value="LED Lighting Upgrade">LED Lighting Upgrade</option>
                      <option value="Reverse-Cycle AC">Reverse-Cycle Air Conditioning</option>
                      <option value="Commercial Solar">Commercial Solar</option>
                      <option value="Free Energy Assessment">Free Energy Assessment</option>
                      <option value="General Enquiry">General Enquiry</option>
                    </select>
                  </div>

                  <div className={styles.field}>
                    <label className={styles.label} htmlFor="message">Message</label>
                    <textarea id="message" name="message" required rows={5}
                      placeholder="Tell us about your project, property, or any questions you have…"
                      value={form.message} onChange={handle}
                      className={styles.textarea}/>
                  </div>

                  {status === 'error' && error && (
                    <div className={styles.errorBox} role="alert">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <circle cx="8" cy="8" r="7" stroke="#c0392b" strokeWidth="1.5"/>
                        <path d="M8 5v3.5M8 10.5h.01" stroke="#c0392b" strokeWidth="1.5" strokeLinecap="round"/>
                      </svg>
                      {error}
                    </div>
                  )}

                  <button type="submit" disabled={status === 'loading'}
                    className={`btn btn-primary btn-lg ${styles.submitBtn}`}>
                    {status === 'loading'
                      ? <><span className={styles.spinner}/> Sending…</>
                      : 'Send Message →'
                    }
                  </button>
                </form>
              </>
            )}
          </div>

          {/* Map + office info */}
          <div className={styles.mapCol}>
            <div className={styles.mapCard}>
              <img src={IMAGES.contactMap} alt="Melbourne city map" className={styles.mapImg}/>
              <div className={styles.mapOverlay}/>
              <div className={styles.mapPin}>
                <svg width="28" height="34" viewBox="0 0 24 28" fill="none">
                  <path d="M12 0C7.58 0 4 3.58 4 8c0 5.5 8 16 8 16s8-10.5 8-16c0-4.42-3.58-8-8-8zm0 11c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3z" fill="#2C4A1E"/>
                </svg>
              </div>
            </div>

            <div className={styles.officeInfo}>
              <h3 className={`h-md ${styles.officeTitle}`}>Melbourne Office</h3>
              <p className="body-sm">20 Timberton Way<br/>Truganina VIC 3029</p>

              <div className={styles.officeHours}>
                <div className={styles.ohRow}><span className={styles.ohDay}>Mon – Fri</span><span className={styles.ohTime}>8:00 AM – 6:00 PM</span></div>
                <div className={styles.ohRow}><span className={styles.ohDay}>Saturday</span><span className={styles.ohTime}>9:00 AM – 2:00 PM</span></div>
                <div className={styles.ohRow}><span className={styles.ohDay}>Sunday</span><span className={styles.ohTime}>Closed</span></div>
              </div>

              <a href="tel:+61390000000" className="btn btn-outline" style={{ marginTop: 8, justifyContent: 'center' }}>
                Call Us Now
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
